// Admin → Quiz (per-course question bank)
// Pick a course from the dropdown, then add questions + answers (marking the correct one)
// to build that course's quiz question bank.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { ListChecks, Plus, Trash2, Edit3, X, CheckCircle2, Loader2, ImagePlus, Upload, Search, ChevronDown, Check } from 'lucide-react';

const inputCls = 'w-full px-3 py-2 rounded-lg ring-1 ring-slate-300 focus:ring-2 focus:ring-amber-400 focus:outline-none text-sm bg-white';
const LETTERS = ['A', 'B', 'C', 'D', 'E', 'F'];

export default function AdminQuiz() {
    const [courses, setCourses] = useState([]);
    const [counts, setCounts] = useState({});
    const [courseId, setCourseId] = useState('');
    const [questions, setQuestions] = useState([]);
    const [loading, setLoading] = useState(false);
    const [editing, setEditing] = useState(null); // question being edited (modal)
    const [importOpen, setImportOpen] = useState(false);
    const [importBusy, setImportBusy] = useState(false);
    const [importResult, setImportResult] = useState(null);
    const [qFlt, setQFlt] = useState({ category: '', language: '' });

    const catOptions = useMemo(() => {
        const m = new Map();
        courses.forEach((c) => { if (c.category) m.set(c.category, c.category_label || c.category); });
        return [...m.entries()].map(([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label));
    }, [courses]);
    const langOptions = useMemo(() => [...new Set(courses.map((c) => c.language || 'en'))].sort(), [courses]);
    const filteredCourses = useMemo(() => courses.filter((c) => {
        if (qFlt.category && c.category !== qFlt.category) return false;
        if (qFlt.language && (c.language || 'en') !== qFlt.language) return false;
        return true;
    }), [courses, qFlt]);

    useEffect(() => {
        Promise.all([api.get('/admin/videos'), api.get('/admin/quiz/counts')])
            .then(([c, k]) => { setCourses(c.data || []); setCounts(k.data || {}); })
            .catch(() => toast.error('Failed to load courses'));
    }, []);

    const loadQuestions = useCallback(async (cid) => {
        if (!cid) { setQuestions([]); return; }
        setLoading(true);
        try {
            const r = await api.get(`/admin/courses/${cid}/questions`);
            setQuestions(r.data.questions || []);
        } catch { toast.error('Failed to load questions'); }
        finally { setLoading(false); }
    }, []);

    useEffect(() => { loadQuestions(courseId); }, [courseId, loadQuestions]);

    const refreshCounts = useCallback(() => {
        api.get('/admin/quiz/counts').then((k) => setCounts(k.data || {})).catch(() => {});
    }, []);

    const createQuestion = async (payload) => {
        const r = await api.post(`/admin/courses/${courseId}/questions`, payload);
        return r.data;
    };

    const saveEdit = async (payload) => {
        await api.patch(`/admin/questions/${editing.id}`, payload);
        return { id: editing.id };
    };

    // Called by the editor once the question (and any image) has been saved.
    const afterSave = (isNew) => {
        toast.success(isNew ? 'Question added' : 'Question updated');
        if (!isNew) setEditing(null);
        loadQuestions(courseId);
        refreshCounts();
    };

    const delQuestion = async (qid) => {
        if (!window.confirm('Delete this question?')) return;
        try {
            await api.delete(`/admin/questions/${qid}`);
            toast.success('Deleted');
            loadQuestions(courseId);
            refreshCounts();
        } catch { toast.error('Delete failed'); }
    };

    const selectedCourse = useMemo(() => courses.find((c) => c.id === courseId), [courses, courseId]);

    const onImportQuiz = async (file) => {
        if (!file) return;
        setImportBusy(true); setImportResult(null);
        try {
            const fd = new FormData();
            fd.append('file', file);
            const r = await api.post('/admin/quiz/import', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            setImportResult(r.data);
            toast.success(`Imported ${r.data.created} question(s) across ${r.data.courses} course(s)`);
            refreshCounts();
            loadQuestions(courseId);
        } catch (err) {
            toast.error(`Import failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setImportBusy(false); }
    };

    const downloadQuizTemplate = () => {
        const headers = ['Course Title', 'Question', 'Option A', 'Option B', 'Option C', 'Option D', 'Correct Option (A/B/C/D)', 'Explanation', 'Points', 'Language'];
        const sample = ['Electrical Safety Basics', 'Which wire colour is Earth in India?', 'Green / Green-Yellow', 'Red', 'Black', 'Blue', 'A', 'Green (or green-yellow) is the earth wire in India.', '10', 'en'];
        const rows = [headers, sample].map((r) => r.map((h) => `"${String(h).replace(/"/g, '""')}"`).join(','));
        const csv = '\ufeff' + rows.join('\n') + '\n';
        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob); a.download = 'quiz_import_template.csv'; a.click();
    };

    return (
        <div className="space-y-6">
            <div className="flex items-start justify-between gap-3">
                <div>
                    <h1 className="font-display font-extrabold text-2xl text-slate-900 flex items-center gap-2">
                        <ListChecks className="w-6 h-6 text-amber-500" /> Quiz
                    </h1>
                    <p className="text-sm text-slate-500">Select a course, then add questions &amp; answers to build its quiz question bank.</p>
                </div>
                <button type="button" onClick={() => { setImportResult(null); setImportOpen(true); }}
                    className="flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-slate-900 text-white text-sm font-bold hover:bg-slate-800 transition"
                    data-testid="quiz-import-btn">
                    <Upload className="w-4 h-4" /> Import quiz
                </button>
            </div>

            {/* Course picker */}
            <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                <label className="text-xs font-bold uppercase tracking-wider text-slate-500">Course</label>
                {/* Field filters to narrow the list before searching (doc Point 26) */}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                    <select value={qFlt.category} onChange={(e) => setQFlt({ ...qFlt, category: e.target.value })}
                        className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="quiz-filter-category">
                        <option value="">All categories</option>
                        {catOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                    <select value={qFlt.language} onChange={(e) => setQFlt({ ...qFlt, language: e.target.value })}
                        className="px-2.5 py-2 rounded-lg ring-1 ring-slate-300 text-sm bg-white" data-testid="quiz-filter-language">
                        <option value="">All languages</option>
                        {langOptions.map((l) => <option key={l} value={l}>{l.toUpperCase()}</option>)}
                    </select>
                    {(qFlt.category || qFlt.language) && (
                        <button type="button" onClick={() => setQFlt({ category: '', language: '' })}
                            className="text-xs text-slate-600 hover:text-slate-900 px-1" data-testid="quiz-filter-clear">Clear</button>
                    )}
                    <span className="text-xs text-slate-400">{filteredCourses.length} course{filteredCourses.length === 1 ? '' : 's'}</span>
                </div>
                <div className="mt-2 flex items-center gap-3 flex-wrap">
                    <CourseCombobox courses={filteredCourses} allCourses={courses} counts={counts} value={courseId} onChange={setCourseId} />
                    {selectedCourse && (
                        <span className="text-sm text-slate-500">{questions.length} question{questions.length === 1 ? '' : 's'} in this bank</span>
                    )}
                </div>
            </div>

            {!courseId && (
                <div className="text-center text-slate-400 text-sm py-12 bg-white rounded-2xl ring-1 ring-slate-200">
                    <ListChecks className="w-8 h-8 mx-auto mb-2 opacity-30" />
                    Choose a course above to manage its quiz questions.
                </div>
            )}

            {courseId && (
                <>
                    {/* Add question */}
                    <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm p-5">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-amber-600 mb-3">
                            <Plus className="w-4 h-4" /> Add a question
                        </div>
                        <QuestionEditor key={`new-${courseId}`} onSave={createQuestion} onDone={() => afterSave(true)} submitLabel="Add question" />
                    </div>

                    {/* Questions list */}
                    <div className="bg-white rounded-2xl ring-1 ring-slate-200 shadow-sm overflow-hidden">
                        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
                            <div className="font-bold text-slate-800">Question bank ({questions.length})</div>
                            {loading && <Loader2 className="w-4 h-4 animate-spin text-slate-400" />}
                        </div>
                        <div className="divide-y divide-slate-100">
                            {questions.length === 0 && !loading && (
                                <div className="px-5 py-10 text-center text-slate-400 text-sm">No questions yet. Add the first one above.</div>
                            )}
                            {questions.map((q, i) => (
                                <div key={q.id} className="px-5 py-4" data-testid={`quiz-q-row-${q.id}`}>
                                    <div className="flex items-start gap-3">
                                        <div className="w-6 h-6 rounded-full bg-slate-100 grid place-items-center text-xs font-bold text-slate-500 mt-0.5 shrink-0">{i + 1}</div>
                                        <div className="flex-1 min-w-0">
                                            <div className="font-bold text-slate-800 text-sm">{q.question}</div>
                                            {q.image_url && <img src={`${process.env.REACT_APP_BACKEND_URL}${q.image_url}`} alt="" className="mt-2 max-h-24 rounded-lg ring-1 ring-slate-200" data-testid={`quiz-q-img-${q.id}`} />}
                                            <ul className="mt-2 space-y-1">
                                                {q.options.map((o, oi) => (
                                                    <li key={oi} className={`text-xs flex items-center gap-2 ${oi === q.correct_index ? 'text-emerald-700 font-bold' : 'text-slate-600'}`}>
                                                        <span className={`w-4 h-4 rounded-full grid place-items-center text-[9px] ${oi === q.correct_index ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>{LETTERS[oi]}</span>
                                                        {o}
                                                        {oi === q.correct_index && <CheckCircle2 className="w-3 h-3 text-emerald-500" />}
                                                    </li>
                                                ))}
                                            </ul>
                                            {q.explanation && <div className="mt-1.5 text-[11px] text-slate-400 italic">Explanation: {q.explanation}</div>}
                                            <div className="mt-1 text-[11px] text-amber-600 font-bold">{q.points || 0} pts</div>
                                        </div>
                                        <button onClick={() => setEditing(q)} className="p-1.5 rounded-lg hover:bg-slate-100" data-testid={`quiz-q-edit-${q.id}`}><Edit3 className="w-4 h-4 text-slate-600" /></button>
                                        <button onClick={() => delQuestion(q.id)} className="p-1.5 rounded-lg hover:bg-rose-50" data-testid={`quiz-q-del-${q.id}`}><Trash2 className="w-4 h-4 text-rose-600" /></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </>
            )}

            {/* Edit modal */}
            {editing && (
                <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={() => setEditing(null)}>
                    <div onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl w-full max-w-xl p-5 ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" data-testid="quiz-edit-modal">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="font-display font-bold text-lg text-slate-900">Edit question</h3>
                            <button onClick={() => setEditing(null)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
                        </div>
                        <QuestionEditor initial={editing} onSave={saveEdit} onDone={() => afterSave(false)} submitLabel="Save changes" />
                    </div>
                </div>
            )}

            {/* Bulk quiz import */}
            {importOpen && (
                <div className="fixed inset-0 z-50 bg-black/55 backdrop-blur-sm grid place-items-center p-4" onClick={() => setImportOpen(false)}>
                    <div className="bg-white rounded-2xl w-full max-w-lg p-5 space-y-3 ring-1 ring-slate-200 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="quiz-import-modal">
                        <div className="flex items-center justify-between">
                            <h3 className="font-display font-bold text-lg text-slate-900">Import quiz (bulk)</h3>
                            <button onClick={() => setImportOpen(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5 text-slate-500" /></button>
                        </div>
                        <p className="text-sm text-slate-500">Upload an <b>.xlsx</b> or <b>.csv</b> to add quiz questions to <b>multiple courses at once</b>. Each row is one question, matched to a course by its <b>Course Title</b>. Columns: Course Title, Question, Option A–D, Correct Option (A/B/C/D), Explanation, Points, Language.</p>
                        <button type="button" onClick={downloadQuizTemplate} className="text-xs font-bold text-amber-600 underline" data-testid="quiz-import-download-template">Download sample CSV template</button>
                        <p className="text-[11px] text-slate-400">Tip: if a Course Title contains a comma, keep it wrapped in quotes (the template already does) or save the file as <b>.xlsx</b> to avoid column shifts.</p>
                        <label className={`flex items-center justify-center gap-2 px-4 py-6 rounded-xl border-2 border-dashed text-sm font-bold ${importBusy ? 'border-slate-200 text-slate-400 cursor-wait' : 'border-amber-300 text-amber-700 hover:bg-amber-50 cursor-pointer'}`} data-testid="quiz-import-dropzone">
                            {importBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            {importBusy ? 'Importing…' : 'Choose .xlsx / .csv file'}
                            <input type="file" accept=".xlsx,.csv" className="hidden" disabled={importBusy}
                                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; onImportQuiz(f); }} data-testid="quiz-import-file-input" />
                        </label>
                        {importResult && (
                            <div className="rounded-xl bg-slate-50 ring-1 ring-slate-200 p-3 text-sm space-y-1" data-testid="quiz-import-result">
                                <div className="font-bold text-emerald-700">Imported: {importResult.created} question(s) across {importResult.courses} course(s)</div>
                                {importResult.skipped > 0 && <div className="text-rose-600 font-bold">Skipped: {importResult.skipped}</div>}
                                {(importResult.errors || []).length > 0 && (
                                    <ul className="text-xs text-slate-500 list-disc pl-4 max-h-32 overflow-y-auto">
                                        {importResult.errors.map((e, i) => <li key={i}>{e}</li>)}
                                    </ul>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

// Point 19 — searchable course picker (type to filter already-uploaded courses).
function CourseCombobox({ courses, allCourses, counts, value, onChange }) {
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const boxRef = useRef(null);
    const selected = (allCourses || courses).find((c) => c.id === value);
    const q = query.trim().toLowerCase();
    const filtered = q ? courses.filter((c) => (c.title || '').toLowerCase().includes(q)) : courses;

    useEffect(() => {
        const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, []);

    return (
        <div ref={boxRef} className="relative w-full max-w-md" data-testid="quiz-course-combobox">
            <button type="button" onClick={() => { setOpen((o) => !o); setQuery(''); }}
                className={`${inputCls} flex items-center justify-between text-left`} data-testid="quiz-course-select">
                <span className={`truncate ${selected ? 'text-slate-800' : 'text-slate-400'}`}>
                    {selected ? `${selected.title} (${counts[selected.id] || 0} Q)` : '— Select a course —'}
                </span>
                <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
            </button>
            {open && (
                <div className="absolute z-30 mt-1 w-full bg-white rounded-xl ring-1 ring-slate-200 shadow-xl overflow-hidden">
                    <div className="p-2 border-b border-slate-100">
                        <div className="relative">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                            <input autoFocus value={query} onChange={(e) => setQuery(e.target.value)}
                                placeholder="Search courses…"
                                className="w-full pl-8 pr-3 py-2 rounded-lg ring-1 ring-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400"
                                data-testid="quiz-course-search" />
                        </div>
                    </div>
                    <div className="max-h-64 overflow-y-auto py-1">
                        {filtered.length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-slate-400">No courses match “{query}”</div>
                        )}
                        {value && (
                            <button type="button" onClick={() => { onChange(''); setOpen(false); }}
                                className="w-full text-left px-3 py-2 text-sm text-slate-400 hover:bg-slate-50" data-testid="quiz-course-option-clear">
                                — Clear selection —
                            </button>
                        )}
                        {filtered.map((c) => (
                            <button key={c.id} type="button" onClick={() => { onChange(c.id); setOpen(false); }}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-amber-50 ${c.id === value ? 'bg-amber-50' : ''}`}
                                data-testid={`quiz-course-option-${c.id}`}>
                                <span className="truncate text-slate-700">{c.title}</span>
                                <span className="text-xs text-slate-400 shrink-0 inline-flex items-center gap-1">
                                    {counts[c.id] || 0} Q {c.id === value && <Check className="w-3.5 h-3.5 text-emerald-500" />}
                                </span>
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

function QuestionEditor({ initial, onSave, onDone, submitLabel }) {
    const [question, setQuestion] = useState(initial?.question || '');
    const [options, setOptions] = useState(initial?.options ? [...initial.options] : ['', '', '', '']);
    const [correct, setCorrect] = useState(initial?.correct_index ?? 0);
    const [explanation, setExplanation] = useState(initial?.explanation || '');
    const [points, setPoints] = useState(initial?.points ?? 10);
    const [busy, setBusy] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [preview, setPreview] = useState(null);
    const [imageUrl, setImageUrl] = useState(initial?.image_url ? `${process.env.REACT_APP_BACKEND_URL}${initial.image_url}` : null);
    const [removeImg, setRemoveImg] = useState(false);

    const setOpt = (i, v) => setOptions((prev) => prev.map((o, idx) => (idx === i ? v : o)));
    const addOpt = () => { if (options.length < 6) setOptions((p) => [...p, '']); };
    const removeOpt = (i) => {
        if (options.length <= 2) return;
        setOptions((prev) => prev.filter((_, idx) => idx !== i));
        setCorrect((c) => (c === i ? 0 : c > i ? c - 1 : c));
    };

    // Never let the "correct" marker sit on an empty option — move it to the first filled one.
    useEffect(() => {
        if (options[correct] && options[correct].trim()) return;
        const firstFilled = options.findIndex((o) => o.trim());
        if (firstFilled >= 0 && firstFilled !== correct) setCorrect(firstFilled);
    }, [options, correct]);

    const onPickImage = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 8 * 1024 * 1024) { toast.error('Image must be under 8MB'); return; }
        setImageFile(f); setRemoveImg(false);
        setPreview(URL.createObjectURL(f));
    };
    const clearImage = () => {
        setImageFile(null); setPreview(null);
        if (imageUrl) setRemoveImg(true);
        setImageUrl(null);
    };

    const submit = async (e) => {
        e.preventDefault();
        const cleaned = options.map((o) => o.trim());
        if (!question.trim()) { toast.error('Question text is required'); return; }
        if (cleaned.filter(Boolean).length < 2) { toast.error('Add at least 2 answer options'); return; }
        if (!cleaned[correct]) { toast.error('The correct option cannot be empty'); return; }
        setBusy(true);
        try {
            const saved = await onSave({ question: question.trim(), options: cleaned, correct_index: correct, explanation: explanation.trim(), points: parseInt(points, 10) || 0 });
            const qid = saved?.id || initial?.id;
            if (qid && imageFile) {
                const fd = new FormData();
                fd.append('file', imageFile);
                await api.post(`/admin/questions/${qid}/image`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
            } else if (qid && removeImg) {
                await api.delete(`/admin/questions/${qid}/image`).catch(() => {});
            }
            if (onDone) onDone();
            if (!initial) {
                setQuestion(''); setOptions(['', '', '', '']); setCorrect(0); setExplanation(''); setPoints(10);
                setImageFile(null); setPreview(null); setImageUrl(null); setRemoveImg(false);
            }
        } catch (err) {
            toast.error(`Failed: ${err?.response?.data?.detail || err.message}`);
        } finally { setBusy(false); }
    };

    return (
        <form onSubmit={submit} className="space-y-3" data-testid="quiz-question-editor">
            <textarea value={question} onChange={(e) => setQuestion(e.target.value)} rows="2" placeholder="Question text" className={inputCls} data-testid="quiz-q-text" />

            {/* Optional question image */}
            <div className="space-y-1.5">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Question image (optional)</div>
                {(preview || imageUrl) ? (
                    <div className="relative inline-block">
                        <img src={preview || imageUrl} alt="Question" className="max-h-40 rounded-lg ring-1 ring-slate-200" data-testid="quiz-image-preview" />
                        <button type="button" onClick={clearImage} className="absolute -top-2 -right-2 w-6 h-6 grid place-items-center rounded-full bg-rose-600 text-white shadow" data-testid="quiz-image-remove"><X className="w-3.5 h-3.5" /></button>
                    </div>
                ) : (
                    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg ring-1 ring-dashed ring-slate-300 text-sm text-slate-600 cursor-pointer hover:ring-amber-400 w-fit">
                        <ImagePlus className="w-4 h-4 text-slate-400" /> Upload image
                        <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={onPickImage} data-testid="quiz-image-input" />
                    </label>
                )}
                <p className="text-[11px] text-slate-400">Shown to the learner above the question. PNG/JPG/WebP, max 8MB.</p>
            </div>

            <div className="space-y-2">
                <div className="text-xs font-bold uppercase tracking-wider text-slate-500">Answer options — select the correct one</div>
                {options.map((o, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <label className={`flex items-center gap-1.5 shrink-0 ${o.trim() ? 'cursor-pointer' : 'cursor-not-allowed'}`} title={o.trim() ? 'Mark as correct' : 'Fill this option before marking it correct'}>
                            <input type="radio" name="correct" checked={correct === i} onChange={() => setCorrect(i)} disabled={!o.trim()} className="disabled:opacity-40 disabled:cursor-not-allowed" data-testid={`quiz-correct-${i}`} />
                            <span className={`w-5 h-5 rounded-full grid place-items-center text-[10px] font-bold ${correct === i ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>{LETTERS[i]}</span>
                        </label>
                        <input value={o} onChange={(e) => setOpt(i, e.target.value)} placeholder={`Option ${LETTERS[i]}`} className={inputCls} data-testid={`quiz-opt-${i}`} />
                        {options.length > 2 && (
                            <button type="button" onClick={() => removeOpt(i)} className="p-1.5 rounded-lg hover:bg-rose-50 shrink-0"><Trash2 className="w-4 h-4 text-rose-500" /></button>
                        )}
                    </div>
                ))}
                {options.length < 6 && (
                    <button type="button" onClick={addOpt} className="text-xs font-bold text-amber-600 inline-flex items-center gap-1" data-testid="quiz-add-option"><Plus className="w-3.5 h-3.5" /> Add option</button>
                )}
            </div>
            <input value={explanation} onChange={(e) => setExplanation(e.target.value)} placeholder="Explanation (optional, shown after answering)" className={inputCls} data-testid="quiz-explanation" />
            <div className="flex items-center justify-between flex-wrap gap-3">
                <label className="text-sm text-slate-700 inline-flex items-center gap-2">
                    Points <input type="number" min="0" value={points} onChange={(e) => setPoints(e.target.value)} className="w-20 px-2 py-1 rounded-md ring-1 ring-slate-300" data-testid="quiz-points" />
                </label>
                <button type="submit" disabled={busy} className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-slate-900 text-white font-bold text-sm hover:bg-slate-700 disabled:opacity-50" data-testid="quiz-save-btn">
                    {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} {submitLabel}
                </button>
            </div>
        </form>
    );
}
